import React from 'react';
import jwt from '../utils/jwt';
import get_url_query from '../utils/url';
import * as Sentry from '@sentry/browser';

import fetcher from "../fetcher/fetch";
import { useTranslation } from 'react-i18next';

import { Alert, Button } from 'react-bootstrap';
import NeoLayout from '../components/neoLayout';
import HTMLContent from '../components/htmlContent';
import GenericStep from './steps/genericStep';
import Actions from '../actions/actions';

import useSentryScope from '../utils/useSentryScope';
import { iframeScrollTo } from '../utils/scroll';

export default function StepManager({ widget=null, widgetKey, firstStep, lang, changeLanguage, token, onStep=()=>{}, onLeave=()=>{}, storage=null }) {
    const tokenTimeout = React.useRef(null);
    const [currentStep, setCurrentStep] = React.useState(firstStep);
    const [stepHistory, setStepHistory] = React.useState([firstStep]);
    const [currentError, setCurrentError] = React.useState(false);
    const [ left, setLeft ] = React.useState(null);
    const [ isSubmitting, setIsSubmitting ] = React.useState(false);
    const [ isGoingBack, setIsGoingBack ] = React.useState(false);
    const [waitingTime, setWaitingTime] = React.useState(0);
    const [, setFailTime] = React.useState(1);

    useSentryScope('step', currentStep.slug);
    const { t } = useTranslation();
    const submitStepAndAdvance = React.useCallback(({ form, forceLang = null, data = {} } = {}, isGoPreviousStep = false) => {
        if (isGoPreviousStep) {
            setIsGoingBack(true);
        } else {
            setIsSubmitting(true);
        }          
        setCurrentError(null);
        const params = {};
        function refreshOnFail(args) {
            return setFailTime(failTime => {
                if (failTime < 6) {
                    setTimeout(() => {
                        submitStepAndAdvance(args);
                    }, failTime * 2000);
                    failTime += 1;
                }
                return failTime;
            });
        }
        // Protection against obscure bug
        let { payload: { current_step }} = jwt.parse(token);
        if (current_step !== currentStep.slug) {
            console.error("submitStepAndAdvance trigerred on obviously erroneous step...");
            return;
        }

        params.headers = params.headers || {};
        params.headers['Authorization'] = `Bearer ${token}`;
        params.headers['Accept-Language'] = forceLang || lang;

        // If we have data to send, send it
        params.data = data;
        if(form) {
            params.data.form = form;
        }

        const query = get_url_query();

        // Send POST request to server to check for current step validity and future steps
        return fetcher.post(`/ticket/widgets/${widgetKey}/steps/${currentStep.slug}${query}`, params).then(({ response, status }) => {   
            if (isGoPreviousStep) {
                setIsGoingBack(false);
            } else {
                setIsSubmitting(false);
            }        
            const history = [...stepHistory];
            history.push(response);
            setStepHistory(history);

            if (response.is_final) {
                //the step is final, we delete the token from the local storage and we don't store it anymore
                storage.remove();
            } else {
                //The step is not final we store in localstorage the new token
                onStep(response);
            }
            setCurrentStep(response);
            iframeScrollTo(document.querySelector(`.wz-neo-layout`));
        }).catch(e => {
            if (isGoPreviousStep) {
                setIsGoingBack(false);
            } else {
                setIsSubmitting(false);
            }  
            if (e.response) {
                try {
                    let response = JSON.parse(e.response);
                    if(response.non_field_errors) {
                        let error = response.non_field_errors && response.non_field_errors.join(', ');
                        setCurrentError(error);
                    } else {
                        let error = Object.values(response).flat(2).join('\n');
                        setCurrentError(error);
                    }

                } catch (e) {
                    setCurrentError(t('widget.generic.errors.unknown'));
                }
            } else {
                refreshOnFail({form, data});
                Sentry.captureException(e);
            }

            if(e.code && [403, 500].includes(e.code)) {
                // Retry if 403 or 500
                if (e.code === 500) {
                    return refreshOnFail({form, data})
                }
                storage.remove();
                window.location.reload();
            }
        });
    }, [ currentStep, widgetKey, stepHistory, token, t, lang, onStep, storage ]);

    const token_autorefresh = React.useCallback(tokenToParse => {
        if(tokenTimeout.current) {
            tokenTimeout.current = clearTimeout(tokenTimeout.current);
        }

        let { payload: { iat, nbf }} = jwt.parse(tokenToParse);

        let last_update = storage.retrieve()?.last_update;
        let autorefresh = Boolean(currentStep.data?.autorefresh_token);
        if(!autorefresh) {
            return;
        }

        //If we have a last_update time in the localstorage, we have to compute the offset
        let token_timeout = Math.floor((nbf - iat) * 1000);
        let tab_timeout = last_update ? Math.abs(Date.now() - last_update) : 0;
        let waiting_time = token_timeout - tab_timeout;
        setWaitingTime(waiting_time);
        tokenTimeout.current = setTimeout(() => {
            if(tokenTimeout.current !== undefined) {
                submitStepAndAdvance();
            }
        }, waiting_time);
    }, [ currentStep, submitStepAndAdvance, tokenTimeout, storage]);

    React.useEffect(() => {
        //To avoid a call to token_autorefresh if the step if final
        if (token && !currentStep.is_final) {
            token_autorefresh(token);
        }
    }, [ token, token_autorefresh, currentStep]);

    // Previous action for HellFest Refund
    const goPreviousStep = React.useCallback(() => {
        let widgetConfig = stepHistory.find((step) => step.slug === currentStep.previous_step_slug);
        if (widgetConfig) {
            setCurrentStep(widgetConfig);

            // Remove current step from history
            let newHistory = [...stepHistory];
            newHistory.pop();
            setStepHistory(newHistory);
        } else {
            // ?
        }
    }, [stepHistory, currentStep]);

    const skip = React.useCallback(() => {
        // ? not handled yet
    }, []);

    const executeActions = React.useCallback(() => {
        if (currentStep && currentStep.data['actions']) {
            let actions = currentStep.data['actions'];
            let promise = actions.reduce((prev, cur) => {

                // Configure scope for sentry
                Sentry.configureScope(scope => {
                    scope.setExtra('step_action', cur.action);
                    scope.setExtra('step_action_args', cur.args);
                });

                if(!Actions[cur.action]) {
                    throw new Error(`Actions: no action named ${cur.action}`);
                }

                return prev.then((previous_action_result) => Actions[cur.action]({ 
                    args: cur.args,
                    previous: previous_action_result,
                    history: stepHistory,
                    step: currentStep,
                    setStep: setCurrentStep,
                    storage
                }));
            }, Promise.resolve());

            promise.catch(e => {
                Sentry.captureException(e);
            });
        }
    }, [ currentStep, storage, stepHistory ]);

    const leave = (step_name) => {
        let confirmed = window.confirm(t('widget.actions.leave', { name: step_name }));
        if(!confirmed) {
            return;
        }

        tokenTimeout.current = clearTimeout(tokenTimeout.current);
        setLeft(step_name);
        onLeave();
    };

    // If for some reason firstStep changes
    // we update it in state to get new data
    // (ex: language changed)
    React.useEffect(() => {
        setCurrentStep(firstStep);
    }, [firstStep]);
    
    React.useEffect(() => {
        executeActions();
    }, [ executeActions ]);

    React.useEffect(() => {
        return () => {
            tokenTimeout.current = clearTimeout(tokenTimeout.current);
          };
    }, [ tokenTimeout ]);

    if (!currentStep || !token) {
        // should we have a default step ? 
        return 'Loading....';
    }

    if(left) {
        return (
            <NeoLayout>
                <NeoLayout.Header config={{title: t('widget.screens.left.title', { name: left || t('widget.screens.left.generic') })}}/>
                <NeoLayout.Description config={{description: t('widget.screens.left.description')}}/>
                <Button variant='primary' onClick={() => window.location.reload()}>{t('widget.screens.left.reload')}</Button>
            </NeoLayout>
        )
    }

    let { payload } = jwt.parse(token);

    return (
        <NeoLayout className={currentStep.slug}>
            {
                currentError && <Alert className="wz-neo-alert-error" variant="danger"><HTMLContent content={currentError}/></Alert>
            }
            <GenericStep
                lang={lang}
                changeLanguage={changeLanguage}
                key={currentStep.slug}
                step={currentStep}
                input={payload}
                token={token}
                onSubmit={submitStepAndAdvance}
                onPreviousStep={goPreviousStep}
                onSkip={skip}
                onLeave={leave}
                widgetKey={widgetKey}
                widget={widget}
                storage={storage}
                waitingTime={waitingTime}
                isSubmitting={isSubmitting}
                isGoingBack={isGoingBack}
            />
        </NeoLayout>
    );
}
